import { get, post } from "@/services/axiosUitils";
import {
  ASSESSMENT_STATUS_CONFIG,
  getStatusEndpoints,
} from "../config/assessmentStatusConfig";
import {
  parseStatusResponse,
  validateStatusResponse,
  createParsingErrorResponse,
} from "../utils/responseParser";
import { StatusCache } from "../utils/statusCache";
import type {
  AssessmentCompletionResponse,
  StatusCheckOptions,
  MethodAttemptResult,
} from "../types/assessmentStatusTypes";

// Re-export types for backward compatibility
export { type AssessmentCompletionResponse } from "../types/assessmentStatusTypes";

/**
 * Assessment Status Service Class
 * Handles assessment completion status checks with caching
 */
class AssessmentStatusService {
  /**
   * Try HTTP method with error handling
   */
  private async tryHttpMethod(
    method: "GET" | "POST",
    endpoint: string
  ): Promise<MethodAttemptResult> {
    try {
      const response =
        method === "GET"
          ? await get<any>(endpoint, {
              showNotification: false,
              skipAuthGate: true,
            })
          : await post<any>(endpoint, {}, {
              showNotification: false,
              skipAuthGate: true,
            });

      return {
        success: true,
        response: response,
        method,
      };
    } catch (error) {
      return {
        success: false,
        error,
        method,
      };
    }
  }

  /**
   * Process successful API response
   */
  private processSuccessfulResponse(
    responseData: any,
    token: string
  ): AssessmentCompletionResponse {
    // Validate response structure
    if (!validateStatusResponse(responseData)) {
      return createParsingErrorResponse();
    }

    // Parse the response
    const parsedResponse = parseStatusResponse(responseData);

    // Cache the result
    StatusCache.set(token, parsedResponse);

    return parsedResponse;
  }

  /**
   * A network/server error worth retrying (as opposed to a definitive answer
   * like 404 = no assessment, or 401 = auth issue handled elsewhere).
   */
  private isTransientError(error: any): boolean {
    const status = error?.response?.status as number | undefined;
    // No response at all → network/CORS/timeout
    if (status === undefined) return true;
    // Server-side hiccups
    if (status >= 500) return true;
    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Single pass: GET (fallback to POST on 405). Returns a parsed response on
   * success, or throws the underlying error so the caller can decide to retry.
   */
  private async attemptCheck(
    token: string
  ): Promise<AssessmentCompletionResponse> {
    const endpoints = getStatusEndpoints();

    const getResult = await this.tryHttpMethod("GET", endpoints.checkCompletion);
    if (getResult.success) {
      return this.processSuccessfulResponse(getResult.response, token);
    }

    // If GET fails with 405 (Method Not Allowed), try POST
    if ((getResult.error as any)?.response?.status === 405) {
      const postResult = await this.tryHttpMethod(
        "POST",
        endpoints.checkCompletion
      );
      if (postResult.success) {
        return this.processSuccessfulResponse(postResult.response, token);
      }
      throw postResult.error ?? getResult.error;
    }

    throw getResult.error;
  }

  /**
   * Check assessment completion status.
   * Retries on transient network/server errors so a temporary connection
   * problem never gets misread as "assessment not completed".
   * Uses common get/post utilities - Token & Tenant-ID auto-injected!
   */
  checkAssessmentCompletion = async (
    token: string,
    options: StatusCheckOptions = {}
  ): Promise<AssessmentCompletionResponse> => {
    // Check cache first (unless force refresh)
    if (options.useCache !== false && !options.forceRefresh) {
      const cached = StatusCache.get(token);
      if (cached) {
        return cached;
      }
    }

    const maxAttempts = 3;
    let lastError: any;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await this.attemptCheck(token);
      } catch (error: any) {
        lastError = error;

        // 404 = definitive "no assessment yet" — do not retry, not an error.
        if (error?.response?.status === 404) {
          return {
            isComplete: false,
            message: "No assessment found - needs to be completed",
            confidence: 0.8,
            source: "error_handling",
          };
        }

        // Only retry transient failures, and only if attempts remain.
        if (this.isTransientError(error) && attempt < maxAttempts) {
          await this.delay(ASSESSMENT_STATUS_CONFIG.RETRY_DELAY_BASE * attempt);
          continue;
        }

        // Non-transient (e.g. auth) or out of retries → surface the error so the
        // caller can show a retry prompt instead of guessing new-vs-returning.
        break;
      }
    }

    throw new Error(
      lastError?.message ?? "Failed to check assessment status"
    );
  };

  /**
   * Clear status cache for user logout
   */
  clearCache = (token?: string): void => {
    if (token) {
      StatusCache.clear(token);
    } else {
      StatusCache.clearAll();
    }
  };

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats = () => {
    return StatusCache.getStats();
  };

  /**
   * Force cleanup of expired cache entries
   */
  forceCleanupCache = (): void => {
    StatusCache.cleanup();
  };
}

// Export singleton instance (City Collab pattern)
const assessmentStatusService = new AssessmentStatusService();
export default assessmentStatusService;
